// Staff authentication against the Laravel API. Platform accounts use the
// central `/api/platform/auth/*` routes; agency staff use `/api/auth/*` on the
// tenant host.
//
// Platform sign-in is always two-step. Agency staff stay single-step until
// they turn on an authenticator from their profile.

import { currentTenantHost, isPlatformHost } from '@/config';
import { staffSession, type StaffSession } from '@/lib/session';

export interface TwoFactorChallenge {
  required: true;
  enrolled: boolean;
  challengeToken: string;
  expiresAt: string;
  secret?: string;
  otpauthUri?: string;
}

export type LoginResult =
  | { session: StaffSession }
  | { challenge: TwoFactorChallenge };

function authHeaders(): Headers {
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' });
  if (!isPlatformHost()) headers.set('X-Tenant-Host', currentTenantHost());
  return headers;
}

function sessionPath(action: 'login' | 'logout'): string {
  return isPlatformHost() ? `/api/platform/auth/${action}` : `/api/auth/${action}`;
}

function errorMessage(payload: unknown, fallback: string): string {
  if (payload !== null && typeof payload === 'object') {
    const record = payload as { error?: unknown; message?: unknown };
    if (typeof record.error === 'string' && record.error.trim() !== '') return record.error;
    if (typeof record.message === 'string' && record.message.trim() !== '') return record.message;
  }
  return fallback;
}

function parseChallenge(value: unknown): TwoFactorChallenge | null {
  if (value === null || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.required !== true || typeof record.challengeToken !== 'string' || typeof record.expiresAt !== 'string') {
    return null;
  }
  return {
    required: true,
    enrolled: record.enrolled === true,
    challengeToken: record.challengeToken,
    expiresAt: record.expiresAt,
    secret: typeof record.secret === 'string' ? record.secret : undefined,
    otpauthUri: typeof record.otpauthUri === 'string' ? record.otpauthUri : undefined,
  };
}

const STAFF_REMEMBER_KEY = 'fleetava_staff_remember';

function staffShouldRemember(): boolean {
  try {
    return window.sessionStorage.getItem(STAFF_REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

export async function login(email: string, password: string, remember = true): Promise<LoginResult> {
  try {
    window.sessionStorage.setItem(STAFF_REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    // The server still receives the flag and limits the token lifetime.
  }
  const res = await fetch(sessionPath('login'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, password, remember }),
  });

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw new Error(errorMessage(data, 'Invalid credentials'));
  }

  const record = data !== null && typeof data === 'object' ? data as Record<string, unknown> : null;
  if (typeof record?.token === 'string' && record.user) {
    const session: StaffSession = { token: record.token, user: record.user as StaffSession['user'] };
    staffSession.set(session, remember);
    return { session };
  }

  const challenge = parseChallenge(record?.twoFactor);
  if (challenge) return { challenge };

  throw new Error('The server did not complete the sign-in.');
}

export async function verifyTwoFactor(challengeToken: string, code: string): Promise<StaffSession> {
  const path = isPlatformHost() ? '/api/platform/auth/two-factor/verify' : '/api/auth/two-factor/verify';
  const res = await fetch(path, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ challengeToken, code }),
  });

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw new Error(errorMessage(data, 'That code is not valid.'));
  }

  const record = data !== null && typeof data === 'object' ? data as Record<string, unknown> : null;
  if (typeof record?.token !== 'string' || !record.user) {
    throw new Error('The server did not complete two-factor verification.');
  }

  const session: StaffSession = { token: record.token, user: record.user as StaffSession['user'] };
  staffSession.set(session, staffShouldRemember());
  return session;
}

function recoveryRoot(): string {
  return isPlatformHost() ? '/api/platform/auth' : '/api/auth';
}

async function postAuth(path: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(path, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    throw new Error(errorMessage(data, 'The request could not be completed.'));
  }
  return data !== null && typeof data === 'object' ? data as Record<string, unknown> : {};
}

export async function requestPasswordReset(email: string): Promise<string | null> {
  const data = await postAuth(`${recoveryRoot()}/password/forgot`, { email });
  return typeof data.demoCode === 'string' ? data.demoCode : null;
}

export async function completePasswordReset(email: string, code: string, password: string, passwordConfirmation: string): Promise<void> {
  await postAuth(`${recoveryRoot()}/password/reset`, {
    email,
    code,
    password,
    password_confirmation: passwordConfirmation,
  });
}

export async function requestAuthenticatorRecovery(challengeToken: string): Promise<string | null> {
  const data = await postAuth(`${recoveryRoot()}/two-factor/recover`, { challengeToken });
  return typeof data.demoCode === 'string' ? data.demoCode : null;
}

export async function confirmAuthenticatorRecovery(challengeToken: string, code: string): Promise<LoginResult> {
  const record = await postAuth(`${recoveryRoot()}/two-factor/recover/verify`, { challengeToken, code });
  if (typeof record.token === 'string' && record.user) {
    const session: StaffSession = { token: record.token, user: record.user as StaffSession['user'] };
    staffSession.set(session, staffShouldRemember());
    return { session };
  }
  const challenge = parseChallenge(record.twoFactor);
  if (challenge) return { challenge };
  throw new Error('The server did not complete authenticator recovery.');
}

export async function logout(): Promise<void> {
  try {
    const headers = authHeaders();
    const token = staffSession.token();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    await fetch(sessionPath('logout'), { method: 'POST', headers });
  } finally {
    staffSession.set(null);
  }
}

export function currentStaff(): StaffSession | null {
  return staffSession.get();
}
